import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatCurrency, todayISO } from "@/lib/format";
import { FormShell, Field, CurrencyInput, AutocompleteInput, MultiCheck } from "@/components/forms/controls";
import EditableSelect from "@/components/EditableSelect";
import MachinePlanEditor, { derivedFromPlan } from "@/components/MachinePlanEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Warning } from "@phosphor-icons/react";

const useList = (entity) => useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data });

export default function StartNewJob() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const jobs = useList("jobs");
  const operations = useList("operations");
  const machines = useList("machines");
  const team = useList("team");
  const [saving, setSaving] = useState(false);
  const [showDateWarn, setShowDateWarn] = useState(false);

  const [form, setForm] = useState({
    client_name: "", job_name: "", product_type: "",
    start_date: todayISO(), end_date: "", budget_allocated: "",
    operations_required: [], machine_plan: [],
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const clientOptions = (jobs.data || []).map((j) => j.client_name);

  const datesInvalid = !!(form.start_date && form.end_date && form.end_date < form.start_date);

  const doCreate = async () => {
    setSaving(true);
    try {
      const plan = (form.machine_plan || []).filter((r) => r.machine_id);
      const { data } = await api.post("/jobs", {
        ...form, machine_plan: plan, ...derivedFromPlan(plan), job_status: "Draft",
      });
      qc.invalidateQueries({ queryKey: ["entity"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Job ${data.code} created as Draft`);
      navigate(`/jobs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!form.client_name.trim() || !form.job_name.trim()) {
      toast.error("Client Name and Job Name are required");
      return;
    }
    if (datesInvalid) {
      setShowDateWarn(true);
      return;
    }
    doCreate();
  };

  return (
    <FormShell title="Start New Job" subtitle="Create a job — it will be saved as a Draft.">
      <Field label="Client Name" required hint="Start typing to pick a previous client">
        <AutocompleteInput value={form.client_name} onChange={(v) => set("client_name", v)} options={clientOptions} placeholder="e.g. Acme Engineering" testId="njob-client" />
      </Field>
      <Field label="Job Name / Description" required>
        <Input value={form.job_name} onChange={(e) => set("job_name", e.target.value)} placeholder="e.g. Progressive stamping die" data-testid="njob-name" />
      </Field>
      <Field label="Product Type">
        <EditableSelect optionsKey="product_type" value={form.product_type} onChange={(v) => set("product_type", v)} testId="njob-product-type" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Date"><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="njob-start" /></Field>
        <Field label="Expected End Date">
          <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={datesInvalid ? "border-red-400 focus-visible:ring-red-400" : ""} data-testid="njob-end" />
          {datesInvalid && (
            <p className="flex items-center gap-1 text-xs text-red-600 mt-1.5" data-testid="njob-date-warning">
              <Warning className="h-3.5 w-3.5" weight="fill" /> End date is before the start date
            </p>
          )}
        </Field>
      </div>
      <Field label="Budget Allocated">
        <CurrencyInput value={form.budget_allocated} onChange={(v) => set("budget_allocated", v)} testId="njob-budget" placeholder="0" />
      </Field>

      <Field label="Operations Required" hint="Standard cost & time shown for reference">
        <MultiCheck
          items={operations.data || []}
          selected={form.operations_required}
          onChange={(v) => set("operations_required", v)}
          getLabel={(o) => `${o.operation_name || o.code}`}
          getSub={(o) => `Std cost ${formatCurrency(o.standard_cost)} · Std time ${o.standard_time_hours || 0}h`}
          testIdPrefix="njob-op"
          empty="No operations defined yet"
        />
      </Field>
      <Field label="Allocate Machines, Planned Hours & Operators" hint="Add a row per machine — planned hours drive the machine load calendar">
        <MachinePlanEditor value={form.machine_plan} onChange={(v) => set("machine_plan", v)} testIdPrefix="njob-mplan" />
      </Field>

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={() => navigate("/")} className="flex-1" data-testid="njob-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="njob-submit">
          {saving ? "Creating..." : "Create Job (Draft)"}
        </Button>
      </div>

      <AlertDialog open={showDateWarn} onOpenChange={setShowDateWarn}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check the dates</AlertDialogTitle>
            <AlertDialogDescription>
              The expected end date is before the start date. Do you want to save this job anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="njob-date-cancel">Go Back &amp; Fix</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowDateWarn(false); doCreate(); }} data-testid="njob-date-confirm">Save Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormShell>
  );
}
